/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useEffect,
  MutableRefObject,
  useImperativeHandle,
} from 'react';

import { useSkipEffectOnMount } from '@hooks/lifecycle';

import { RadioGroupStyleProps, RadioWrapper } from './styled';
import Radio from './next/RadioInput';

export type ResetSelectionFNRef = MutableRefObject<() => void>;
export type Props = {
  resetRef: ResetSelectionFNRef;
  selectIndex: React.Dispatch<React.SetStateAction<number>>;
};
export type RadioGroupProps<T = any> = {
  /** Function called when an option is selected.
   * if `getOption` props is passed, its return value will be passed
   * to `onChange`, otherwise the return value of `getValue` props
   */
  onChange?: <Key>(value: Key | T, index?: number) => void;
  /** the value selected by default */
  selectedIndex?: number;
  /** Array of options */
  options?: T[];
  /** Function to compute the label to be displayed for complex option types */
  getLabel?: (option: T) => React.ReactNode;
  /** Function to compute the key value associated to an option*/
  getValue?: (option: T, index?: number) => string;
  /**Function to compute an option from the key */
  getOption?: (key: string) => [T, number];
  renderRadioElement?: (
    radioElement: React.ReactNode,
    option: T,
    key: unknown,
    onSelect?: () => void
  ) => React.ReactNode;
  resetSelectionFnRef?: ResetSelectionFNRef;
  style?: React.CSSProperties;
  name?: string;
  disabled?: boolean;
  booker?: boolean;
} & Partial<RadioGroupStyleProps>;

const MIN_SHIFT = -3; // help in resetting the selected index

const UseReset = ({ selectIndex, resetRef }: Props) => {
  useImperativeHandle(
    resetRef,
    () => () => selectIndex((x) => (x >= 0 ? -1 : MIN_SHIFT - x)),
    [selectIndex]
  );
  return null;
};

export const RadioGroup = ({
  direction,
  onChange,
  selectedIndex: initialSelectedIndex = -1,
  options,
  getLabel = (x) => x,
  getValue = (x) => x as string,
  getOption = (x) => [x, -1],
  resetSelectionFnRef,
  renderRadioElement = (radioElement) => radioElement,
  name,
  disabled,
  booker,
  ...rest
}: RadioGroupProps) => {
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const [selectedValue, setSelectedValue] = useState(() => {
    const option = options?.[selectedIndex] ?? undefined;
    return option ? getValue(option, selectedIndex) : undefined;
  });

  const updateInternal = (e: any, index?: number): void => {
    let { value } = e.target;

    if (value === 'true' || value === 'false') {
      value = JSON.parse(value);
    }

    setSelectedValue(value);

    onChange?.(value, index);
  };
  useSkipEffectOnMount(() => {
    setSelectedIndex(initialSelectedIndex);
  }, [initialSelectedIndex]);

  useEffect(() => {
    const option = options?.[selectedIndex] ?? undefined;

    setSelectedValue(option ? getValue(option, selectedIndex) : undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);
  return (
    <RadioWrapper direction={direction} disabled={disabled}>
      {options?.map((option, idx) => {
        const label = getLabel?.(option);
        const value = getValue?.(option, idx);
        return renderRadioElement(
          <Radio
            disabled={disabled}
            key={value}
            label={label}
            name={name}
            booker={booker}
            onChange={(e) => updateInternal(e, idx)}
            checked={Boolean(value === selectedValue)}
            {...rest}
            value={value}
          />,
          option,
          idx,
          () => {
            setSelectedValue(value);
            onChange?.(...getOption(value));
          }
        );
      })}
      {resetSelectionFnRef ? (
        <UseReset
          resetRef={resetSelectionFnRef}
          selectIndex={setSelectedIndex}
        />
      ) : null}
    </RadioWrapper>
  );
};

export default RadioGroup;
